import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import Header from "../../../components/Header";
import sendgridSyncService from "../../../api/services/sendgridSync.service";

const POLL_MS = 5000;

const RUN_LABELS = {
  success: { label: "Succeeded", chip: "bg-green-100 text-green-800" },
  partial: { label: "Finished with problems", chip: "bg-amber-100 text-amber-800" },
  failed: { label: "Failed", chip: "bg-red-100 text-red-800" },
  skipped: { label: "Skipped", chip: "bg-gray-100 text-gray-700" },
};

const KIND_LABELS = {
  status: "Family status",
  area: "Area",
  program: "Program",
  class: "Class",
};

const formatEastern = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const n = (value) => Number(value || 0).toLocaleString("en-US");

// SendGrid's own wording, turned into what the office should do about it.
const explainError = (error) => {
  if (!error) return null;
  if (/access forbidden|scopes/i.test(error)) {
    return 'SendGrid refused the API key. In SendGrid, go to Settings → API Keys, edit the key the app uses, and give it "Marketing" full access. The key needs the "Marketing" permission to manage contacts and lists.';
  }
  return error;
};

function summaryLine(summary = {}) {
  const parts = [`${n(summary.contacts)} contacts updated`];
  if (summary.members_added) parts.push(`${n(summary.members_added)} added to lists`);
  if (summary.members_removed) parts.push(`${n(summary.members_removed)} removed from lists`);
  if (summary.lists_created) parts.push(`${n(summary.lists_created)} lists created`);
  if (summary.lists_deleted) parts.push(`${n(summary.lists_deleted)} lists removed`);
  if (summary.invalid_emails) parts.push(`${n(summary.invalid_emails)} skipped (bad email)`);
  return parts.join(" · ");
}

export default function SendGridSync() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await sendgridSyncService.status());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load the sync status.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // While a run is going, keep checking until it finishes.
  const running = Boolean(data?.running);
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [running, load]);

  const syncNow = async () => {
    setStarting(true);
    try {
      await sendgridSyncService.run();
      toast.success("Sync started. It takes a few minutes.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the sync.");
    } finally {
      await load();
      setStarting(false);
    }
  };

  const last = data?.last_run;
  const lastStyle = last ? RUN_LABELS[last.status] || RUN_LABELS.skipped : null;
  const lastProblem = last ? explainError(last.error) : null;

  return (
    <div className="h-full flex flex-col">
      <Header />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">SendGrid Sync</h1>
            <p className="mt-1 text-sm text-gray-600 max-w-2xl">
              Every family in the app is copied into SendGrid Marketing → Contacts, sorted
              into lists by area, program and class. Families move between lists as they
              enroll and leave. Runs every night, or now with the button.
            </p>
          </div>
          <button
            type="button"
            onClick={syncNow}
            disabled={!data?.live || running || starting}
            className="inline-flex items-center gap-2 rounded-lg bg-[#173151] px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />
            Sync now
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        {data && !data.enabled && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            The sync is turned off on the server, so SendGrid is not being updated.
          </div>
        )}
        {data && data.enabled && !data.has_api_key && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            No SendGrid API key is set on the server.
          </div>
        )}

        {running && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            Syncing now, started {formatEastern(data.running.started_at)}. This page updates by itself.
          </div>
        )}

        {last && (
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${lastStyle.chip}`}>
                {`Last sync: ${lastStyle.label}`}
              </span>
              <span className="text-sm text-gray-600">{formatEastern(last.finished_at || last.started_at)}</span>
            </div>
            {last.status !== "skipped" && last.status !== "failed" && (
              <p className="mt-2 text-sm text-gray-800">{summaryLine(last.summary)}</p>
            )}
            {lastProblem && <p className="mt-2 text-sm text-red-700">{lastProblem}</p>}
            {(last.summary?.errors || []).map((problem) => (
              <p key={problem} className="mt-1 text-sm text-amber-800">{problem}</p>
            ))}
          </section>
        )}

        <section className="rounded-lg border border-gray-200 bg-white">
          <h2 className="border-b border-gray-200 px-4 py-3 text-base font-semibold text-gray-900">
            Lists in SendGrid
          </h2>
          {data?.lists?.length ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-4 py-2 font-medium">Group</th>
                  <th className="px-4 py-2 font-medium">List name</th>
                  <th className="px-4 py-2 text-right font-medium">Families</th>
                </tr>
              </thead>
              <tbody>
                {data.lists.map((item) => (
                  <tr key={`${item.kind}-${item.name}`} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-gray-600">{KIND_LABELS[item.kind] || item.kind}</td>
                    <td className="px-4 py-2 text-gray-900">{item.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n(item.members)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-4 py-3 text-sm text-gray-600">No lists yet. They appear after the first sync.</p>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-base font-semibold text-gray-900">Fields to filter on</h2>
            <p className="mt-1 text-sm text-gray-600">
              Every contact carries these. In SendGrid, use Marketing → Contacts → Create → New
              Segment, pick a field and a condition (for example csf_classes contains
              "Footprints") to target any group the lists don&apos;t cover.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium">Field</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">What it holds</th>
              </tr>
            </thead>
            <tbody>
              {(data?.fields || []).map((field) => (
                <tr key={field.name} className="border-t border-gray-100">
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{field.name}</td>
                  <td className="px-4 py-2 text-gray-600">{field.type}</td>
                  <td className="px-4 py-2 text-gray-800">{field.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
